"use client"

import useSWRBasketSessionState from "@/app/(site)/_utils/_hooks/SWR/useSWRBasketSessionState"
import { isPfasShippingStateAndZipRestricted } from "@/data/pfas-zipcode-state-restrictions"
import { PFAS_COUNTRY } from "@/lib/siteconfig/constants"
import { useMemo } from "react"

interface UsePfasValidationProps {
    country?: string
    state?: string
    zipCode?: string
    disabled?: boolean
}

interface UsePfasValidationReturn {
    isPfasRestricted: boolean
    restrictedProducts: Array<{
        productName: string
        pfasRestrictions: Array<{ label: string; value: string }>
    }>
}

/**
 * Custom hook to validate PFAS restrictions for shipping location
 * Checks if any products in the cart have PFAS restrictions for the given state/zip combination
 */
export const usePfasValidation = ({
    country,
    state,
    zipCode,
    disabled = false
}: UsePfasValidationProps): UsePfasValidationReturn => {
    const { selection } = useSWRBasketSessionState()

    const validationResult = useMemo(() => {
        // If validation is disabled, return early
        if (disabled) {
            return {
                isPfasRestricted: false,
                restrictedProducts: []
            }
        }

        const zipCodeTrimmed = zipCode?.trim()
        // Only validate for US country
        if (!country || country !== PFAS_COUNTRY) {
            return {
                isPfasRestricted: false,
                restrictedProducts: []
            }
        }

        // Need both state and zipCode for validation
        if (!state || !zipCodeTrimmed) {
            return {
                isPfasRestricted: false,
                restrictedProducts: []
            }
        }

        // Get cart items from selection
        const cartItems = selection?.items || []

        if (cartItems.length === 0) {
            return {
                isPfasRestricted: false,
                restrictedProducts: []
            }
        }

        // Check each cart item for PFAS restrictions
        const restrictedProducts: Array<{
            productName: string
            pfasRestrictions: Array<{ label: string; value: string }>
        }> = []

        //@todo: fix this type
        cartItems.forEach((item: any) => {
            if (item?.product?.pfas_state_restriction) {
                const isRestricted = isPfasShippingStateAndZipRestricted(
                    item.product.pfas_state_restriction,
                    state,
                    zipCodeTrimmed,
                    country
                )

                if (isRestricted) {
                    restrictedProducts.push({
                        productName: item.product.name || "Unknown Product",
                        pfasRestrictions: item.product.pfas_state_restriction
                    })
                }
            }
        })

        return {
            isPfasRestricted: restrictedProducts.length > 0,
            restrictedProducts
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [country, state, zipCode, disabled, JSON.stringify(selection?.items)])

    return validationResult
}
